import { useEffect, useState } from "react";
import { Page, Card, Field, Input, Select, Button, Money, Badge, Feedback, Table, Empty } from "../../ui";
import { send, entityRows } from "../../lib/api";
import { useAccounts, useCustomers, useVendors } from "../../lib/hooks";
import { statusTone, today } from "../../lib/format";
import { t } from "../../i18n";

const BU = "NUMEN";
function fb0() { return { ok: null as boolean | null, msg: "" }; }
function genId(prefix: string) { return prefix + String(Date.now()).slice(-7); }

/* ── Accounts Receivable: customer invoices + payments ─────────────────── */
export function ArPage() {
  const customers = useCustomers();
  const cashAccounts = useAccounts().filter((a) => a.acct_type === "A");
  const [items, setItems] = useState<Record<string, string>[]>([]);
  const [fb, setFb] = useState(fb0());
  const [cust, setCust] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("CAD");
  const [descr, setDescr] = useState("");
  // Payment-receipt state.
  const [payItem, setPayItem] = useState("");
  const [payAmt, setPayAmt] = useState("");
  const [cashAcct, setCashAcct] = useState("");
  const load = () => entityRows<Record<string, string>>("ps_ar_pending_item").then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  useEffect(() => { const c = customers.find((x) => x.customer_id === cust); if (c) setCurrency(c.currency_cd); }, [cust, customers]);
  const openItems = items.filter((it) => it.item_status === "OPEN" || it.item_status === "PARTIAL");
  useEffect(() => { const it = openItems.find((x) => x.item_id === payItem); if (it) setPayAmt(it.balance_amt); /* eslint-disable-next-line */ }, [payItem]);

  const create = async () => {
    setFb(fb0());
    try {
      const id = genId("INV");
      await send("POST", "/api/v1/ar/items", {
        business_unit: BU, item_id: id, customer_id: cust, item_dt: today(), due_dt: today(),
        entry_type: "IN", entry_amt: Number(amount).toFixed(2), currency_cd: currency, descr,
      }, true);
      setFb({ ok: true, msg: t("ar.created", { id }) }); setAmount(""); setDescr(""); load();
    } catch (e) { setFb({ ok: false, msg: String((e as Error).message) }); }
  };

  const receivePayment = async () => {
    const it = openItems.find((x) => x.item_id === payItem);
    if (!it || !cashAcct) return;
    setFb(fb0());
    try {
      const deposit = genId("DEP");
      await send("POST", "/api/v1/ar/payments", {
        business_unit: BU, deposit_id: deposit, payment_seq_num: 1, customer_id: it.customer_id,
        setid: it.setid, payment_amt: Number(payAmt).toFixed(2), payment_method: "EFT",
        payment_dt: today(), currency_cd: it.currency_cd, reference_num: deposit,
        applications: [{ item_id: it.item_id, applied_amt: Number(payAmt).toFixed(2) }],
      }, true);
      await send("POST", `/api/v1/ar/payments/${deposit}/1/post`, { cash_account: cashAcct }, true);
      setFb({ ok: true, msg: t("ar.paymentPosted", { id: it.item_id }) });
      setPayItem(""); setPayAmt(""); load();
    } catch (e) { setFb({ ok: false, msg: String((e as Error).message) }); }
  };

  return (
    <Page title={t("nav.ar")}>
      <div className="report-cols">
        <Card title={t("ar.newInvoice")}>
          <Field label={t("common.customer")}>
            <Select value={cust} onChange={(e) => setCust(e.target.value)}>
              <option value="">{t("common.selectCustomer")}</option>
              {customers.map((c) => <option key={c.customer_id} value={c.customer_id}>{c.customer_name}</option>)}
            </Select>
          </Field>
          <Field label={t("common.amount")}><Input className="num" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
          <Field label={t("common.currency")}><Select value={currency} onChange={(e) => setCurrency(e.target.value)}><option>CAD</option><option>USD</option><option>EUR</option></Select></Field>
          <Field label={t("common.description")}><Input value={descr} onChange={(e) => setDescr(e.target.value)} /></Field>
          <div className="form-actions"><Button disabled={!cust || !amount} onClick={create}>{t("ar.createInvoice")}</Button></div>
          <Feedback ok={fb.ok} message={fb.msg} />
        </Card>
        <Card title={t("ar.openItems")}>
          {items.length ? (
            <Table head={[t("ar.col.item"), t("common.customer"), t("common.amount"), t("ar.col.balance"), t("common.status")]}>
              {items.map((it) => (
                <tr key={it.item_id}><td>{it.item_id}</td><td>{it.customer_id}</td><td className="num"><Money value={it.entry_amt} currency={it.currency_cd} /></td><td className="num"><Money value={it.balance_amt} currency={it.currency_cd} /></td><td><Badge tone={statusTone(it.item_status)}>{it.item_status}</Badge></td></tr>
              ))}
            </Table>
          ) : <Empty>{t("ar.noItems")}</Empty>}
        </Card>
      </div>
      <Card title={t("ar.receivePayment")}>
        <div className="form-row">
          <Field label={t("ar.openItem")}>
            <Select value={payItem} onChange={(e) => setPayItem(e.target.value)}>
              <option value="">{t("ar.selectItem")}</option>
              {openItems.map((it) => <option key={it.item_id} value={it.item_id}>{it.item_id} — {it.customer_id} ({it.balance_amt})</option>)}
            </Select>
          </Field>
          <Field label={t("ar.paymentAmount")}><Input className="num" inputMode="decimal" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} /></Field>
          <Field label={t("ar.cashAccount")}>
            <Select value={cashAcct} onChange={(e) => setCashAcct(e.target.value)}>
              <option value="">{t("common.selectAccount")}</option>
              {cashAccounts.map((a) => <option key={a.account} value={a.account}>{a.account} — {a.descr}</option>)}
            </Select>
          </Field>
        </div>
        <div className="form-actions"><Button disabled={!payItem || !payAmt || !cashAcct} onClick={receivePayment}>{t("ar.receiveApply")}</Button></div>
      </Card>
    </Page>
  );
}

/* ── Accounts Payable: vendor bills ───────────────────────────────────── */
export function ApPage() {
  const vendors = useVendors();
  const accounts = useAccounts().filter((a) => a.acct_type === "E");
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [fb, setFb] = useState(fb0());
  const [vendor, setVendor] = useState("");
  const [account, setAccount] = useState("");
  const [amount, setAmount] = useState("");
  const [invoice, setInvoice] = useState("");
  const load = () => entityRows<Record<string, string>>("ps_voucher").then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const create = async () => {
    setFb(fb0());
    try {
      const id = genId("VCH");
      await send("POST", "/api/v1/ap/vouchers", {
        business_unit: BU, voucher_id: id, vendor_id: vendor, invoice_id: invoice || id, invoice_dt: today(),
        accounting_dt: today(), currency_cd: "CAD", lines: [{ account, merchandise_amt: Number(amount).toFixed(2) }],
      }, true);
      await send("POST", `/api/v1/ap/vouchers/${id}/post?bu=${BU}`, {}, true);
      setFb({ ok: true, msg: t("ap.posted", { id }) }); setAmount(""); setInvoice(""); load();
    } catch (e) { setFb({ ok: false, msg: String((e as Error).message) }); }
  };
  return (
    <Page title={t("nav.ap")}>
      <div className="report-cols">
        <Card title={t("ap.enterBill")}>
          <Field label={t("common.vendor")}><Select value={vendor} onChange={(e) => setVendor(e.target.value)}><option value="">{t("common.selectVendor")}</option>{vendors.map((v) => <option key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</option>)}</Select></Field>
          <Field label={t("ap.vendorInvoice")}><Input value={invoice} onChange={(e) => setInvoice(e.target.value)} /></Field>
          <Field label={t("ap.expenseAccount")}><Select value={account} onChange={(e) => setAccount(e.target.value)}><option value="">{t("common.selectAccount")}</option>{accounts.map((a) => <option key={a.account} value={a.account}>{a.account} — {a.descr}</option>)}</Select></Field>
          <Field label={t("common.amount")}><Input className="num" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
          <div className="form-actions"><Button disabled={!vendor || !account || !amount} onClick={create}>{t("ap.enterPost")}</Button></div>
          <Feedback ok={fb.ok} message={fb.msg} />
        </Card>
        <Card title={t("ap.bills")}>
          {rows.length ? (
            <Table head={[t("ap.col.voucher"), t("common.vendor"), t("ap.col.gross"), t("ap.col.net"), t("common.status")]}>
              {rows.map((v) => <tr key={v.voucher_id}><td>{v.voucher_id}</td><td>{v.vendor_id}</td><td className="num"><Money value={v.gross_amt} /></td><td className="num"><Money value={v.net_amt} /></td><td><Badge tone={statusTone(v.voucher_status)}>{v.voucher_status}</Badge></td></tr>)}
            </Table>
          ) : <Empty>{t("ap.noBills")}</Empty>}
        </Card>
      </div>
    </Page>
  );
}

/* ── Billing: sales invoices (revenue + GST) ──────────────────────────── */
export function BiPage() {
  const customers = useCustomers();
  const revenue = useAccounts().filter((a) => a.acct_type === "R");
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [fb, setFb] = useState(fb0());
  const [cust, setCust] = useState(""); const [acct, setAcct] = useState("");
  const [amount, setAmount] = useState(""); const [tax, setTax] = useState("");
  const load = () => entityRows<Record<string, string>>("ps_bi_header").then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const create = async () => {
    setFb(fb0());
    try {
      const id = genId("BIL");
      await send("POST", "/api/v1/bi/invoices", {
        business_unit: BU, invoice_id: id, customer_id: cust, invoice_dt: today(), accounting_dt: today(), due_dt: today(),
        currency_cd: "CAD", lines: [{ charge_code: "SALE", unit_price: Number(amount).toFixed(2), line_amt: Number(amount).toFixed(2), tax_amt: Number(tax || 0).toFixed(2), revenue_account: acct }],
      }, true);
      await send("POST", `/api/v1/bi/invoices/${id}/post?bu=${BU}`, {}, true);
      setFb({ ok: true, msg: t("bi.issued", { id }) }); setAmount(""); setTax(""); load();
    } catch (e) { setFb({ ok: false, msg: String((e as Error).message) }); }
  };
  return (
    <Page title={t("nav.bi")}>
      <div className="report-cols">
        <Card title={t("bi.issueInvoice")}>
          <Field label={t("common.customer")}><Select value={cust} onChange={(e) => setCust(e.target.value)}><option value="">{t("common.selectCustomer")}</option>{customers.map((c) => <option key={c.customer_id} value={c.customer_id}>{c.customer_name}</option>)}</Select></Field>
          <Field label={t("bi.revenueAccount")}><Select value={acct} onChange={(e) => setAcct(e.target.value)}><option value="">{t("common.selectAccount")}</option>{revenue.map((a) => <option key={a.account} value={a.account}>{a.account} — {a.descr}</option>)}</Select></Field>
          <Field label={t("bi.amountBeforeTax")}><Input className="num" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
          <Field label={t("bi.gst")}><Input className="num" inputMode="decimal" value={tax} onChange={(e) => setTax(e.target.value)} /></Field>
          <div className="form-actions"><Button disabled={!cust || !acct || !amount} onClick={create}>{t("bi.issue")}</Button></div>
          <Feedback ok={fb.ok} message={fb.msg} />
        </Card>
        <Card title={t("bi.invoices")}>
          {rows.length ? (
            <Table head={[t("bi.col.invoice"), t("common.customer"), t("common.total"), t("common.status")]}>
              {rows.map((b) => <tr key={b.invoice_id}><td>{b.invoice_id}</td><td>{b.customer_id}</td><td className="num"><Money value={b.total_amt} /></td><td><Badge tone={statusTone(b.bill_status)}>{b.bill_status}</Badge></td></tr>)}
            </Table>
          ) : <Empty>{t("bi.noInvoices")}</Empty>}
        </Card>
      </div>
    </Page>
  );
}

/* ── Expenses: expense reports ────────────────────────────────────────── */
export function ExPage() {
  const accounts = useAccounts().filter((a) => a.acct_type === "E");
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [fb, setFb] = useState(fb0());
  const [emp, setEmp] = useState(""); const [acct, setAcct] = useState("");
  const [amount, setAmount] = useState(""); const [descr, setDescr] = useState("");
  const load = () => entityRows<Record<string, string>>("ps_ex_sheet_hdr").then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const create = async () => {
    setFb(fb0());
    try {
      const id = genId("ER");
      await send("POST", "/api/v1/ex/reports", {
        business_unit: BU, er_id: id, emplid: emp, report_descr: descr, accounting_dt: today(), currency_cd: "CAD",
        lines: [{ expense_type: "MISC", expense_dt: today(), monetary_amount: Number(amount).toFixed(2), account: acct }],
      }, true);
      setFb({ ok: true, msg: t("ex.submitted", { id }) }); setAmount(""); setDescr(""); load();
    } catch (e) { setFb({ ok: false, msg: String((e as Error).message) }); }
  };
  const act = async (er: string, action: "approve" | "post") => {
    try {
      await send("POST", `/api/v1/ex/reports/${er}/${action}${action === "post" ? `?bu=${BU}` : ""}`, {}, action === "post");
      setFb({ ok: true, msg: t(action === "approve" ? "ex.approved" : "ex.posted", { id: er }) }); load();
    } catch (e) { setFb({ ok: false, msg: String((e as Error).message) }); }
  };
  return (
    <Page title={t("nav.ex")}>
      <div className="report-cols">
        <Card title={t("ex.newReport")}>
          <Field label={t("ex.employee")}><Input value={emp} onChange={(e) => setEmp(e.target.value)} placeholder={t("ex.employeePlaceholder")} /></Field>
          <Field label={t("ap.expenseAccount")}><Select value={acct} onChange={(e) => setAcct(e.target.value)}><option value="">{t("common.selectAccount")}</option>{accounts.map((a) => <option key={a.account} value={a.account}>{a.account} — {a.descr}</option>)}</Select></Field>
          <Field label={t("common.amount")}><Input className="num" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
          <Field label={t("ex.purpose")}><Input value={descr} onChange={(e) => setDescr(e.target.value)} /></Field>
          <div className="form-actions"><Button disabled={!emp || !acct || !amount} onClick={create}>{t("ex.submit")}</Button></div>
          <Feedback ok={fb.ok} message={fb.msg} />
        </Card>
        <Card title={t("ex.reports")}>
          {rows.length ? (
            <Table head={[t("ex.col.report"), t("ex.employee"), t("ex.col.reimbursable"), t("common.status"), t("common.actions")]}>
              {rows.map((r) => (
                <tr key={r.er_id}>
                  <td>{r.er_id}</td><td>{r.emplid}</td><td className="num"><Money value={r.reimbursable_amt} /></td>
                  <td><Badge tone={statusTone(r.er_status)}>{r.er_status}</Badge></td>
                  <td>
                    {!["APPROVED", "POSTED", "PAID"].includes(r.er_status) && <button className="link-btn" onClick={() => act(r.er_id, "approve")}>{t("ex.approve")}</button>}
                    {r.er_status === "APPROVED" && <button className="link-btn" onClick={() => act(r.er_id, "post")}>{t("ex.post")}</button>}
                  </td>
                </tr>
              ))}
            </Table>
          ) : <Empty>{t("ex.noReports")}</Empty>}
        </Card>
      </div>
    </Page>
  );
}
